import _ from 'lodash';
import { makeObservable, override } from 'mobx';
import {
    CreateLeadRequest,
    LeadApiFindAllLeadsRequest,
    LeadDto,
    LeadDtoBrief,
    UpdateLeadRequest,
} from 'src/generated-api-client';
import { bpmsResourcesManagerApi, leadsApi } from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export type LeadsFilter = ListFilter<LeadApiFindAllLeadsRequest>;

export class LeadsStoreClass extends BasicStore<
    LeadDtoBrief,
    LeadsFilter,
    UpdateLeadRequest,
    CreateLeadRequest,
    LeadDto
> {
    constructor() {
        super();
        makeObservable(this);
    }

    itemDetailsFormKey = 'embedded:deployment:forms/ap/ap-06-lead.json';

    itemEditFormKey = 'embedded:deployment:forms/ap/ap-07-edit-lead.json';

    itemCreateFormKey = 'embedded:deployment:forms/ap/ap-05-create-lead.json';

    pager = new Pager();
    sorter = new Sorter();
    filterCriteria: FilterCriteria<LeadsFilter> = new FilterCriteria();

    api: BasicStoreApi<
        LeadDtoBrief,
        UpdateLeadRequest,
        CreateLeadRequest,
        LeadDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);
            const resp = await RequestHelper.unwrapFromAxiosPromise(
                leadsApi.findAllLeads(
                    {
                        ...this.pager,
                        ...this.sorter,
                        ...this.filterCriteria.filter,
                    },
                    options,
                ),
            );

            this.pager.setTotal(resp.totalElements || 0);

            return resp.content || [];
        },
        loadItemForm: (formKey: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                bpmsResourcesManagerApi.getForm({ formKey }, options),
            );
        },
        loadItem: (id: number, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                leadsApi.getLead({ id }, options),
            );
        },
        updateItem: (id, data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                leadsApi.updateLead(
                    { id: Number(id), updateLeadRequest: data },
                    options,
                ),
            );
        },
        createItem: (data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                leadsApi.createLead({ createLeadRequest: data }, options),
            );
        },
    };

    @override get hasFilter() {
        const { filterSearch } = this.filterCriteria.filter;

        return !_.isNil(filterSearch);
    }
}

export const LeadsStore = new LeadsStoreClass();
